/**
 * @file HomePage.jsx
 * @description A professionally designed, fully responsive home page for Marmasco Technologies.
 *              Features include a hero section with a background image and overlay, service highlights for
 *              Domain Registration and Cloud VPS Hosting, and a "Why Choose Us" section emphasizing our
 *              reliability, security, and instant deployment with no setup fees.
 *              This version includes an animated loading spinner until SEO data is ready and an error screen if data loading fails.
 * @author
 *   Masimba Maregedze
 * @version 1.5.13
 */

import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Spinner, Button } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaServer,
  FaGlobe,
  FaCloud,
  FaShieldAlt, // Added for Security
  FaHeadset, // Added for Support
  FaRocket, // Added for Performance
} from "react-icons/fa";
import serversImage from "../assets/servers.png";
import sharedImage from "../assets/hosting.png";
import domainImage from "../assets/domains.png";
import { runData } from "../functions/api/processor";
import DomainCarousel from "../components/DomainCourosel";
import PageHeader from "../components/PageHeader";

// Define a common style for card images to ensure equal sizing and proper spacing
const cardImageStyle = {
  height: "300px", // Fixed height for equal image display
  objectFit: "cover", // Ensures the image covers the container uniformly
  padding: "10px", // Adds spacing so the image doesn't touch the card edges
  borderRadius: "20px",
};

// Define styles for the service card hover effect
const serviceCardStyle = {
  position: "relative",
  overflow: "hidden",
  borderRadius: "15px", // Add border radius to the card itself
  transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out", // Added shadow transition
};

// Add consistent card body style for equal height content and justified layout
const cardBodyStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  minHeight: "300px", // Adjusted minHeight
  padding: "1.5rem", // Added padding to card body
};

// New HoverCard component for modern hover animation
const HoverCard = (props) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Card
      {...props}
      className={`${props.className} border-0`} // Ensure border-0 is applied
      style={{
        ...props.style,
        ...serviceCardStyle, // Apply base styles
        transform: hovered ? "scale(1.03)" : "scale(1)", // Slightly reduced scale
        boxShadow: hovered
          ? "0 10px 20px rgba(0,0,0,0.15)"
          : "0 4px 8px rgba(0,0,0,0.1)", // Enhanced shadow on hover
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {props.children}
    </Card>
  );
};

/**
 * HomePage component renders the landing page with multiple sections optimized for SEO and user engagement.
 *
 * @returns {JSX.Element} The fully rendered home page component, an animated loading screen, or an error screen based on API response.
 */
const HomePage = () => {
  // State for SEO data, loading status, and error handling.
  const [seoData, setSeoData] = useState({
    title: "Loading...",
    meta_description: "",
    keywords: "",
    og_title: "",
    og_description: "",
    og_image: "",
    canonical_url: window.location.href,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Use runData with 'GET' method for SEO data
        const response = await runData({ page: "home" }, "/web/pages/", "GET");
        if (response.status === 200 && response.data.seo) {
          let seoResponse = response.data.seo;
          if (
            seoResponse.canonical_url &&
            seoResponse.canonical_url.startsWith("/")
          ) {
            let hostname = window.location.hostname;
            if (!hostname.startsWith("www.")) {
              hostname = "www." + hostname;
            }
            seoResponse.canonical_url =
              window.location.protocol +
              "//" +
              hostname +
              seoResponse.canonical_url;
          }
          setSeoData(seoResponse);
        }
      } catch (err) {
        console.error("Failed to load SEO data:", err);
        setError(err.message || "An error occurred while loading SEO data.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Render an animated loading spinner while SEO data is being fetched.
  if (loading) {
    return (
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  // Render an error screen if the SEO data fails to load.
  if (error) {
    return (
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
        style={{ height: "100vh" }}
      >
        <h2>Error: {error}</h2>
      </Container>
    );
  }

  // Once SEO data is loaded successfully, render the full page.
  return (
    <React.Fragment>
      <Helmet>
        <title>{seoData.title} - Marmasco Technologies</title>
        <meta name="description" content={seoData.meta_description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta property="og:title" content={seoData.og_title} />
        <meta property="og:description" content={seoData.og_description} />
        <meta property="og:image" content={seoData.og_image} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={seoData.canonical_url} />
        <script type="application/ld+json">
          {`
            {
              "@context": "http://schema.org",
              "@type": "WebPage",
              "name": "${seoData.title} - Marmasco Technologies",
              "url": "${seoData.canonical_url}",
              "description": "${seoData.meta_description}"
            }
          `}
        </script>
      </Helmet>
      {/* Header Section */}
      <PageHeader seoData={seoData} />
      {/* Main Content Section (Services) */}
      <Container
        id="content-start"
        // Use bg-light for this section only, revert to rounded-4
        className="bg-light py-5 fade-in mb-4 rounded-4"
        style={{
          marginTop: "-6rem",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Section Header */}
        <Row className="mb-5">
          <Col className="text-center">
            <h2 className="display-5 fw-bold mb-3 section-title">
              Our Core Services
            </h2>
            <p
              className="lead text-muted mx-auto"
              style={{ maxWidth: "800px" }}
            >
              Power your online presence with our reliable and high-performance
              digital solutions. From securing your domain to hosting your
              applications, we provide instant setup, zero fees, and expert
              support.
            </p>
          </Col>
        </Row>

        {/* Services Cards */}
        <Row className="px-md-4 px-2 py-3">
          {/* Cloud VPS Hosting Service Card */}
          <Col md={4} className="mb-4">
            <HoverCard className="h-100 bg-light">
              <Card.Img
                variant="top"
                src={serversImage}
                alt="Cloud VPS Hosting"
                style={cardImageStyle}
              />
              <Card.Body style={cardBodyStyle}>
                <div>
                  <Card.Title className="text-center mb-3">
                    {/* Reverted to h5 with section-title class */}
                    <h5 className="section-title">
                      <FaServer className="me-2" />
                      Cloud VPS Hosting
                    </h5>
                  </Card.Title>
                  <Card.Text
                    className="text-muted"
                    style={{ textAlign: "justify" }}
                  >
                    Experience unparalleled performance with our
                    enterprise-grade Cloud VPS. Enjoy 99.9% uptime, rapid
                    deployment, robust security, and dedicated 24/7 support for
                    your critical applications.
                  </Card.Text>
                </div>
                <Button
                  as={Link}
                  to="/servers/"
                  variant="primary"
                  className="mt-4 w-100"
                >
                  Explore VPS Plans{" "}
                  <FaArrowRight style={{ marginLeft: "5px" }} />
                </Button>
              </Card.Body>
            </HoverCard>
          </Col>

          {/* Domain Registration Service Card */}
          <Col md={4} className="mb-4">
            <HoverCard className="h-100 bg-light">
              <Card.Img
                variant="top"
                src={domainImage}
                alt="Domain Registration Service"
                style={cardImageStyle}
              />
              <Card.Body style={cardBodyStyle}>
                <div>
                  <Card.Title className="text-center mb-3">
                    {/* Reverted to h5 with section-title class */}
                    <h5 className="section-title">
                      <FaGlobe className="me-2" />
                      Domain Registration
                    </h5>
                  </Card.Title>
                  <Card.Text
                    className="text-muted"
                    style={{ textAlign: "justify" }}
                  >
                    Secure your brand online with hassle-free domain
                    registration. We offer competitive pricing, easy management,
                    proactive monitoring, and expert advice to find your perfect
                    domain name.
                  </Card.Text>
                </div>
                <Button
                  as={Link}
                  to="/domains/"
                  variant="primary"
                  className="mt-4 w-100"
                >
                  Find Your Domain{" "}
                  <FaArrowRight style={{ marginLeft: "5px" }} />
                </Button>
              </Card.Body>
            </HoverCard>
          </Col>

          {/* Shared Hosting Service Card */}
          <Col md={4} className="mb-4">
            <HoverCard className="h-100 bg-light">
              <Card.Img
                variant="top"
                src={sharedImage}
                alt="Shared Hosting"
                style={cardImageStyle}
              />
              <Card.Body style={cardBodyStyle}>
                <div>
                  <Card.Title className="text-center mb-3">
                    {/* Reverted to h5 with section-title class */}
                    <h5 className="section-title">
                      <FaCloud className="me-2" />
                      Shared Hosting
                    </h5>
                  </Card.Title>
                  <Card.Text
                    className="text-muted"
                    style={{ textAlign: "justify" }}
                  >
                    Affordable and reliable Shared Hosting perfect for websites
                    and professional emails. Benefit from an intuitive control
                    panel, 99.9% uptime guarantee, and our friendly 24/7
                    customer support.
                  </Card.Text>
                </div>
                <Button
                  as={Link}
                  to="/hosting/"
                  variant="primary"
                  className="mt-4 w-100"
                >
                  View Hosting Packages{" "}
                  <FaArrowRight style={{ marginLeft: "5px" }} />
                </Button>
              </Card.Body>
            </HoverCard>
          </Col>
        </Row>
      </Container>{" "}
      {/* End of the first Container */}
      {/* Why Choose Us Section (Separate Container/Row) */}
      <Container fluid className="px-0">
        {" "}
        {/* Added px-0 to prevent double padding if nested */}
        <Row className="mt-5 px-md-4 px-2">
          {/* Remove bg-light from this div to use the default body background */}
          <div className="rounded-4 py-5 my-4 px-md-4 px-2">
            <Col className="text-center">
              <h2 className="display-5 fw-bold mb-4 section-title">
                Why Choose Marmasco?
              </h2>
              <p
                className="lead text-muted mx-auto mb-5"
                style={{ maxWidth: "800px" }}
              >
                We are committed to providing top-tier digital solutions backed
                by exceptional service and reliability you can trust.
              </p>
            </Col>
            <Row className="text-center justify-content-center">
              {/* Feature 1: Reliability */}
              <Col md={4} className="mb-4">
                <FaRocket size={40} className="text-primary mb-3" />
                <h5 className="fw-bold">Unmatched Performance</h5>
                <p className="text-muted px-3">
                  Leverage our enterprise-grade infrastructure for blazing-fast
                  speeds and guaranteed 99.9% uptime for your services.
                </p>
              </Col>
              {/* Feature 2: Security */}
              <Col md={4} className="mb-4">
                <FaShieldAlt size={40} className="text-primary mb-3" />
                <h5 className="fw-bold">Robust Security</h5>
                <p className="text-muted px-3">
                  Your digital assets are protected with advanced security
                  measures, proactive monitoring, and regular updates.
                </p>
              </Col>
              {/* Feature 3: Support */}
              <Col md={4} className="mb-4">
                <FaHeadset size={40} className="text-primary mb-3" />
                <h5 className="fw-bold">Expert 24/7 Support</h5>
                <p className="text-muted px-3">
                  Our dedicated support team is available around the clock to
                  assist you with any technical challenges or questions.
                </p>
              </Col>
            </Row>
          </div>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default HomePage;
